import Callout from "components/callout/callout"
import useTargetStorage from "@/hooks/useTargetStorage"
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, memo } from "react"
import { useTranslation } from "react-i18next"
import { Location } from "types"
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as AddIcon } from 'assets/icons/add.svg'
import { useFormContext } from '../forms/form'
import useLayout from "hooks/useLayout"

interface LocationSelectorProps {
    locations: Location[]
    allLocations: Location[]
    selectedLocation?: string
    selectedLocations?: string[]
    setSelectedLocation?: Dispatch<SetStateAction<string>>
    setSelectedLocations?: Dispatch<SetStateAction<string[]>>
    hiddenLocations?: string[]
    allowCreate?: boolean
}

const LocationSelector:React.FC<LocationSelectorProps> = ({
    locations,
    allLocations,
    selectedLocation,
    selectedLocations,
    setSelectedLocation,
    setSelectedLocations,
    hiddenLocations = [],
    allowCreate = true
}) => {

    const  { t } = useTranslation()
    const { disableInputs, isLoading } = useFormContext()
    const { targetStorageDocId: storageId } = useTargetStorage()
    const { openDialog } = useLayout()

    const scrollRef = useRef<HTMLDivElement>(null)

    const locationMap = useMemo(() => {
        const map = new Map<string, Location>()
        allLocations.forEach(item => {
            if (item.id) {
                map.set(item.id, item)
            }
        })
        return map
    }, [allLocations])

    const currentLocationPath = useMemo(() => {
        if (selectedLocations && selectedLocations.length > 0) {
            return [...selectedLocations]
        } else if (selectedLocation && selectedLocation !== '_') {
            let locationPath = [selectedLocation]
            const visited = new Set<string>()
            let currentLocationId = selectedLocation

            while (locationMap.get(currentLocationId)?.relations?.parentLocation) {
                const parentLocationId = locationMap.get(currentLocationId)?.relations?.parentLocation
                if (parentLocationId && !visited.has(parentLocationId)) {
                    visited.add(parentLocationId)
                    locationPath.unshift(parentLocationId)
                    currentLocationId = parentLocationId
                } else {
                    break
                }
            }
            return locationPath
        }
        return []
    }, [selectedLocation, selectedLocations, locationMap])

    useEffect(() => {
        if (scrollRef.current && currentLocationPath.length > 0) {
            setTimeout(() => {
                if (scrollRef.current) {
                    scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
                }
            }, 0)
        }
    }, [currentLocationPath])

    const getPossibleRootLocations = useMemo(() => {
        let rootLocations = [] as Location[]
        for (const item of allLocations) {
            let isRoot = true
            let current = item as Location | null
            while (current && current.relations?.parentLocation) {
                const parentLocationId = current.relations.parentLocation
                current = allLocations.find(loc => loc.id === parentLocationId) || null
                if (current) {
                    isRoot = false
                    break       
                } else {
                    break
                }
            }
            if (isRoot && item.id && !hiddenLocations.includes(item.id)) {
                rootLocations.push(item)
            }
        }
        return rootLocations
    }, [allLocations, hiddenLocations])

    const getPossibleChildLocations = (parentLocationId: string) => {
        let childLocations = [] as Location[]
        for (const item of allLocations) {
            if (item.relations?.parentLocation === parentLocationId) {
                childLocations.push(item)
            } else {
                let current = item as Location | null
                while (current && current.relations?.parentLocation) {
                    if (current.relations.parentLocation === parentLocationId) {
                        childLocations.push(item)
                        break
                    } else if (!current.isDeleted) {
                        break
                    }
                    const currentParentId = current ? current.relations?.parentLocation : undefined
                    const parentLocation = currentParentId ? allLocations.find(loc => loc.id === currentParentId) : undefined
                    current = parentLocation || null
                }
            }
        }
        return childLocations.filter(item => item.id && !hiddenLocations.includes(item.id))
    }

    const toggleTag = (tagId: string, level: number) => {
        let newLocationPath: string[]

        if (level === 0) {
            if (currentLocationPath[0] === tagId) {
                newLocationPath = []
            } else {
                newLocationPath = [tagId]
            }
        } else {
            newLocationPath = [...currentLocationPath]
            
            if (newLocationPath.length > level + 1) {
                newLocationPath = newLocationPath.slice(0, level + 1)
            }
            
            if (newLocationPath[level] === tagId) {
                newLocationPath = newLocationPath.slice(0, level)
            } else {
                newLocationPath[level] = tagId
                newLocationPath = newLocationPath.slice(0, level + 1)
            }
        }

        const lastLocation = newLocationPath[newLocationPath.length - 1] || ''
        
        if (setSelectedLocations) {
            setSelectedLocations(newLocationPath)
        }
        
        if (setSelectedLocation) {
            setSelectedLocation(lastLocation)
        }
    }

    const colClasses = `flex flex-col gap-2 rounded-md bg-white p-2 border-gray-300 border-[1px]`

    const rowDefaultClasses = `rounded-md p-2 select-none min-w-64 flex justify-between items-center [&>svg]:inline-block [&>svg]:w-5 [&>svg]:h-5 [&>svg]:ml-2 ${disableInputs ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isLoading ? 'animate-pulse' : ''}`
    const rowSelectedClasses = `bg-blue-500 text-white`
    const rowDeletedSelectedClasses = `bg-red-500 text-white [&>svg]:fill-white`
    const rowDeletedUnselectedClasses = `bg-red-200 ${disableInputs && `hover:bg-red-300`} [&>svg]:fill-black`

    const rowUnselectedClasses = `bg-gray-200 text-gray-800 ${disableInputs && `hover:bg-gray-300`}`
    const labelRowClasses = `text-gray-500 text-sm font-semibold select-none text-nowrap`

    const handleCreateLocation = (parentLocationId: string) => {
        if (!storageId || disableInputs) return
        openDialog('create_location', { storageId, parentLocationId })
    }

    const renderCreateButton = (parentLocationId: string) => {
        if (!allowCreate || !storageId) return null
        return (
            <div
                className={`${rowDefaultClasses} ${rowUnselectedClasses} border border-dashed border-gray-400`}
                onClick={(event) => {
                    event.stopPropagation()
                    handleCreateLocation(parentLocationId)
                }}
            >
                <span>{t('buttonCreateLocation')}</span>
                <AddIcon />
            </div>
        )
    }

    if (!locations.length && !allowCreate) {
        return <Callout type="warning">{t('calloutNoLocationsToSelect')}</Callout>
    }

    return (
        <>
            <div className="overflow-x-scroll w-full no-swipe" ref={scrollRef}>
                <div className="flex flex-row gap-2 w-full">
                    <div className={colClasses}>
                        <div className={labelRowClasses}>{`${t(`labelLocationLevel`)}: ${1}`}</div>
                        {getPossibleRootLocations.map((item) => {
                            return (
                                <div
                                    key={item.id}
                                    className={`${rowDefaultClasses} ${currentLocationPath?.includes(item.id || '') ? item.isDeleted ? rowDeletedSelectedClasses : rowSelectedClasses : item.isDeleted ? rowDeletedUnselectedClasses : rowUnselectedClasses}`}
                                    onClick={() => !disableInputs && item.id && toggleTag(item.id, 0)}>
                                    <span className="">{item.name}</span>
                                    {item.isDeleted && <DeleteIcon />}
                                </div>
                            )
                        })}
                        {renderCreateButton('_')}
                    </div>
                    {currentLocationPath && currentLocationPath.map((tagId, key) => {
                        const childLocations = getPossibleChildLocations(tagId)
                        if (childLocations.length === 0 && !allowCreate) return null
                        return (
                            <div className={colClasses} key={tagId}>
                                <div className={labelRowClasses}>{`${t(`labelLocationLevel`)}: ${key + 2}`}</div>
                                {childLocations.map((item) => {
                                    return (
                                        <div
                                            key={item.id}
                                            className={`${rowDefaultClasses} ${currentLocationPath?.includes(item.id || '') ? item.isDeleted ? rowDeletedSelectedClasses : rowSelectedClasses : item.isDeleted ? rowDeletedUnselectedClasses : rowUnselectedClasses}`}
                                            onClick={() => !disableInputs && item.id && toggleTag(item.id, key + 1)}>
                                            <span className="">{item.name}</span>
                                            {item.isDeleted && <DeleteIcon />}
                                        </div>
                                    )
                                })}
                                {renderCreateButton(tagId)}
                            </div>
                        )
                    })}
                </div>
            </div>
            {allLocations.some(item => item.isDeleted) && (
                <div className="mt-2">
                    <Callout type="warning">
                        {t('calloutSomeLocationsDeleted')}
                    </Callout>
                </div>
            )}
        </>
    )

}

export default memo(LocationSelector)
